import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Admin } from '@/types/auth';
import type { AppState } from '@/types/app-state';

const getNextSelectedWholesalerId = (
  user: Admin | null,
  currentSelectedWholesalerId: string | null,
): string | null => {
  if (!user?.wholesalers || user.wholesalers.length === 0) {
    return null;
  }

  if (
    currentSelectedWholesalerId &&
    user.wholesalers.some((wholesaler) => wholesaler.id === currentSelectedWholesalerId)
  ) {
    return currentSelectedWholesalerId;
  }

  return user.wholesalers[0]?.id ?? null;
};

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // Theme
        colorMode:
          (typeof window !== 'undefined'
            ? (localStorage.getItem('pingco-color-mode') as 'light' | 'dark' | null)
            : null) ?? 'light',
        toggleColorMode: () => {
          set((state) => {
            const next = state.colorMode === 'light' ? 'dark' : 'light';
            if (typeof window !== 'undefined') {
              localStorage.setItem('pingco-color-mode', next);
            }
            return { colorMode: next };
          });
        },

        // Sidebar
        navCollapsed: false,
        toggleNavCollapsed: () => {
          set((state) => ({ navCollapsed: !state.navCollapsed }));
        },

        // Notifications
        notifications: [],
        addNotification: (notification) => {
          set((state) => ({
            notifications: [...state.notifications, { ...notification, id: crypto.randomUUID() }],
          }));
        },
        removeNotification: (id) => {
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }));
        },

        // Auth
        isAuthenticated: false,
        setIsAuthenticated: (value) => {
          set({ isAuthenticated: value });
        },

        // Session timestamp
        sessionStartTime: null,
        startSession: () => {
          set({ sessionStartTime: Date.now() });
        },

        // Current user
        currentUser: null,
        selectedWholesalerId: null,
        setCurrentUser: (user) => {
          set((state) => ({
            currentUser: user,
            selectedWholesalerId: getNextSelectedWholesalerId(user, state.selectedWholesalerId),
          }));
        },
        setSelectedWholesalerId: (wholesalerId) => {
          set((state) => {
            if (
              !state.currentUser?.wholesalers.some((wholesaler) => wholesaler.id === wholesalerId)
            ) {
              return {};
            }

            return { selectedWholesalerId: wholesalerId };
          });
        },
        setProfileSetupComplete: (value) => {
          set((state) => {
            if (!state.currentUser) return {};
            return {
              currentUser: { ...state.currentUser, profileSetupComplete: value },
            };
          });
        },
        addWholesaler: (wholesaler) => {
          set((state) => {
            if (!state.currentUser) return {};
            const already = state.currentUser.wholesalers.some((w) => w.id === wholesaler.id);
            if (already) return {};
            return {
              selectedWholesalerId: wholesaler.id,
              currentUser: {
                ...state.currentUser,
                wholesalers: [...state.currentUser.wholesalers, wholesaler],
              },
            };
          });
        },

        // Clear all per-session state on sign-out
        clearSession: () => {
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('wasp-setup');
          }
          set({
            currentUser: null,
            selectedWholesalerId: null,
            isAuthenticated: false,
            sessionStartTime: null,
          });
        },
      }),
      {
        name: 'pingco-auth',
        partialize: (state) => ({
          isAuthenticated: state.isAuthenticated,
          currentUser: state.currentUser,
          selectedWholesalerId: state.selectedWholesalerId,
          navCollapsed: state.navCollapsed,
          sessionStartTime: state.sessionStartTime,
        }),
        version: 1,
        migrate: (persisted: unknown) => {
          const s = persisted as Record<string, unknown>;
          if (s.currentUser != null) {
            const user = s.currentUser as Record<string, unknown>;
            if (!Array.isArray(user.wholesalers)) {
              // Stale persisted shape — wholesalers was undefined before type change.
              // Clear session so user logs in fresh and gets proper data from the API.
              return {
                ...s,
                isAuthenticated: false,
                currentUser: null,
                selectedWholesalerId: null,
                sessionStartTime: null,
              };
            }
          }
          return s;
        },
      },
    ),
    { name: 'app-store' },
  ),
);
